import { useCallback, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useStartAPetitionPageState } from 'src/app/pages/startAPetitionV2/pageContext';
import { useAutocompleteService } from 'src/app/shared/hooks/googlePlaces';
import { useLocale } from 'src/app/shared/hooks/l10n';
import { useMappedLoadedState } from 'src/app/shared/hooks/state';
import { isLoaded } from 'src/app/shared/utils/async';

import { getGooglePlaces, type GooglePlace } from './api';

export function useLocationField() {
	const {
		data: { draft },
	} = useStartAPetitionPageState();
	const locale = useLocale();
	const autocompleteService = useAutocompleteService();

	const { location } = draft?.petitionDraft || {};
	const initialPlaceValue = useMemo(() => {
		if (!location) return null;

		const { id, name } = location;

		if (!id || !name) return null;

		return { id, name };
	}, [location]);
	const [placeInputValue, setPlaceInputValue] = useState('');
	const [placeValue, setPlaceValue] = useState<GooglePlace | null>(initialPlaceValue);

	const query = useQuery({
		queryKey: [autocompleteService, 'places', placeInputValue, locale],
		queryFn: async () => getGooglePlaces({ text: placeInputValue, language: locale }, autocompleteService),
		enabled: !!autocompleteService && placeInputValue.length > 0,
	});

	const placesState = useMappedLoadedState(query, ({ data }) => ({ value: data }));
	const places = useMemo(() => (isLoaded(placesState) ? placesState.value : []), [placesState]);

	const handlePlaceInputChange = useCallback((newValue: string) => {
		setPlaceInputValue(newValue);
	}, []);

	const handlePlaceChange = useCallback((newValue: GooglePlace | null) => {
		setPlaceValue(newValue);
	}, []);

	return {
		data: {
			places,
			placeInputValue,
			placeValue,
		},
		actions: {
			handlePlaceInputChange,
			handlePlaceChange,
		},
	};
}
